import { AxiosError } from 'axios';
import isEmpty from 'lodash/isEmpty';
import { useState, useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Link, Navigate, useNavigate, useLocation, useParams,
} from 'react-router-dom';
import {
  Alert, Row, Col,
} from 'reactstrap';

import appConfig from '@config/app';
import {
  registerRequest,
} from '@reducers/auth/AuthAction';
import { handleError } from '@services/ErrorHandler';
import LocaleService from '@services/LocaleService';
import { CustomButton } from '@shared/buttons';
import Checkbox from '@shared/components/input/Checkbox';
import useTranslation from '@shared/hooks/useTranslation';

import AccountCreatedPage from './AccountCreatedPage';
import ClinicInputFields from './fields/ClinicInputFields';
import DentistInputFields from './fields/DentistInputFields';
import LaboratoryInputFields from './fields/LaboratoryInputFields';

import './RegisterPageContentStyle.scss';

const registrationTypes = [ 'dentist', 'dental-clinic', 'laboratory' ] as const;
type RegistrationType = typeof registrationTypes[number];

const isRegistrationType = (type?: string): type is RegistrationType => (
  registrationTypes.includes(type as RegistrationType)
);

const RegisterPageContent = () => {
  const { type } = useParams<{ type: string }>();
  const registrationType = isRegistrationType(type) ? type : null;
  const [ isRegistering, setIsRegistering ] = useState(false);
  const [ accountCreated, setAccountCreated ] = useState(false);
  const i18nRegistration = useTranslation('registration');
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();

  const invitedEmail = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('email') || '';
  }, [ location.search ]);

  const [ formData, setFormData ] = useState<any>({});
  const [ errors, setErrors ] = useState<any>([]);
  const [ terms, setTerms ] = useState(false);

  const RegistrationComponent = {
    dentist: <DentistInputFields
      formData={formData}
      setFormData={(e: any) => setFormData(e)}
      errors={errors}
      setErrors={(e: any) => setErrors(e)}
      prefilledEmail={invitedEmail}
    />,
    'dental-clinic': <ClinicInputFields
      formData={formData}
      setFormData={(e: any) => setFormData(e)}
      errors={errors}
      prefilledEmail={invitedEmail}
    />,
    laboratory: <LaboratoryInputFields
      formData={formData}
      setFormData={(e: any) => setFormData(e)}
      errors={errors}
      prefilledEmail={invitedEmail}
    />,
  };

  const isPasswordValid = useMemo(() => {
    const isValidLength = formData?.password?.length >= 8;
    const isValidCase = /[A-Z]/.test(formData?.password);
    const isValidNumber = /[0-9]/.test(formData?.password);
    // include all special characters except a-z and 0-9
    const isValidChar = /[^A-Za-z0-9]/.test(formData?.password);
    return isValidLength && isValidCase && isValidNumber && isValidChar;
  }, [ formData?.password ]);

  const isFieldsEmpty = useMemo(() => Object.values(formData).some(
    (value) => isEmpty(value)),
  [ formData ]);

  const handleRegister = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!registrationType) return;

    setIsRegistering(true);
    if (!terms && !isFieldsEmpty) {
      setErrors({
        ...errors,
        terms: i18nRegistration.error.terms,
      });
      setIsRegistering(false);
      return;
    }
    try {
      const data = {
        ...formData,
        type: registrationType,
      };
      await dispatch(registerRequest(data)).$promise;
      setAccountCreated(true);
    } catch (error) {
      const { response: errorResponse } = error as AxiosError;

      if (errorResponse) {
        if (errorResponse.status === 422) {
          const { errors: errorData } = errorResponse.data;
          setErrors({
            ...errorData,
            terms: !terms ? i18nRegistration.error.terms : '',
            password: isPasswordValid || isFieldsEmpty
              ? errorData.password
              : [ i18nRegistration.error.invalidPassword ],
          });
          return;
        }
        handleError(error);
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const handleSelectTerms = () => {
    setTerms((prevTerms) => !prevTerms);
    setErrors({
      ...errors,
      terms: '',
    });
  };

  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo(0, 0);
  }, []);

  if (!registrationType) {
    return <Navigate to="/auth/register" replace />;
  }

  return (
    <div className="registration-page-content">
      {
        !accountCreated && registrationType === 'dental-clinic' && (
          <Row className="mb-4">
            <Col>
              <Alert color="info">
                {i18nRegistration.types.clinic.trialNotice}
              </Alert>
            </Col>
          </Row>
        )
      }
      {
        !accountCreated && RegistrationComponent[registrationType]
      }
      {
        !accountCreated && (
          <Row className="mt-5">
            <Col>
              <div className="terms-check mb-3">
                <Checkbox
                  type="checkbox"
                  checked={terms}
                  onInputChangeHandler={handleSelectTerms}
                  label={(
                    <div className="terms">
                      <span className="text-danger">* </span>
                      {LocaleService.newParseTranslation(i18nRegistration.input.terms, {
                        terms: (
                          <Link to={appConfig.termsAndConditions} className="terms-link" target="_blank">
                            {i18nRegistration.termsAndConditions}
                          </Link>
                        ),
                        privacyPolicy: (
                          <Link to={appConfig.privacyPolicy} className="terms-link" target="_blank">
                            {i18nRegistration.privacyPolicy}
                          </Link>
                        ),
                      })}
                    </div>
                  )}
                  fieldError={errors?.terms}
                  formGrpClassName="terms-form-group"
                />
              </div>
              <CustomButton
                id="btn-register"
                className="btn-lg w-100"
                loading={isRegistering}
                color="primary"
                type="submit"
                onClick={handleRegister}
              >
                {i18nRegistration.button.register}
              </CustomButton>
            </Col>
          </Row>
        )
      }
      {
        accountCreated && (
          <>
            <AccountCreatedPage registrationType={registrationType} />
            <Row className="mt-5">
              <Col>
                <CustomButton
                  id="btn-go-to-login"
                  className="btn-lg w-100"
                  color="primary"
                  onClick={() => navigate('/auth/login')}
                >
                  {i18nRegistration.button.goToLogin}
                </CustomButton>
              </Col>
            </Row>
          </>
        )
      }
    </div>
  );
};

export default RegisterPageContent;
